import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useLogin, useCheckAuthStatus} from '../api/auth';
import {pageRoutes} from '../routes';

const AuthContext = createContext(null);

export const AuthProvider = ({children}) => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const loginMutation = useLogin();

  // Memoize isAuthPage to prevent recalculation on every render
  const isAuthPage = useMemo(() => {
    return (
      location.pathname === '/login' ||
      location.pathname === '/register' ||
      location.pathname === '/forgot-password' ||
      location.pathname === '/reset-password'
    );
  }, [location.pathname]);

  const {data: authData, isLoading: isAuthChecking} = useCheckAuthStatus(
    undefined,
    {enabled: !isAuthPage},
  );

  // Check for stored auth data on mount only
  useEffect(() => {
    const storedAuth = localStorage.getItem('auth');
    if (storedAuth) {
      try {
        const parsed = JSON.parse(storedAuth);
        setUser(parsed.user);
      } catch {
        setUser(null);
      }
    }
  }, []);

  // Handle auth status changes - removed location from dependencies
  useEffect(() => {
    // Only process auth data when we're not on auth pages and not loading
    if (!isAuthChecking && !isAuthPage) {
      if (authData?.data) {
        // Auth is valid, ensure user is set from localStorage
        const storedAuth = localStorage.getItem('auth');
        if (storedAuth) {
          try {
            const parsed = JSON.parse(storedAuth);
            setUser(parsed.user);
          } catch {
            setUser(null);
          }
        }
      } else {
        // Auth failed, clear everything and redirect
        setUser(null);
        localStorage.removeItem('auth');
        navigate('/login', {replace: true});
      }
    }
  }, [authData, isAuthChecking, isAuthPage, navigate]);

  const login = async (credentials) => {
    try {
      const response = await loginMutation.mutateAsync(credentials);
      const userData = response.data;
      setUser(userData);
      localStorage.setItem(
        'auth',
        JSON.stringify({token: userData.token, user: userData, isLogin: true}),
      );
      navigate('/dashboard');
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth');
    navigate('/login');
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isAuthChecking,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
